import baseApi from '@store/api/baseApi';

import { getStageList } from '@/features/task-configuration/redux/taskConfigurationSlice';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { toastError } from '@/components/Toast';

export const taskConfigurationApiSlice = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getTaskConfigurations: builder.query({
			query: ({ params }) => ({
				url: 'product_action/list?includeDisabled=true',
				method: 'GET',
				params: params || {}
			}),
			keepUnusedDataFor: 0,
			providesTags: ['GET_TASK_CONFIGURATIONS_TAG'],
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const { data } = await queryFulfilled;
					dispatch(getStageList(data));
				} catch (error) {
					toastError(error?.data?.message || 'Something went wrong');
				} finally {
					dispatch(closeLoader());
				}
			}
		}),
		createNewTask: builder.mutation({
			query: ({ body, params }) => ({
				url: 'product_action/add',
				method: 'POST',
				body,
				params
			}),
			invalidatesTags: ['GET_TASK_CONFIGURATIONS_TAG', 'GET_TASK_WITH_DEPENDENCY_TAG']
		}),
		updateExistingTask: builder.mutation({
			query: ({ body, query, taskId }) => ({
				url: `/product_action/${taskId}`,
				method: 'PUT',
				body,
				query
			}),
			invalidatesTags: ['GET_TASK_CONFIGURATIONS_TAG', 'GET_TASK_WITH_DEPENDENCY_TAG']
		}),
		getTaskStage: builder.query({
			query: () => ({
				url: '/product_action/stage/list'
			})
		}),
		getTaskWithDependency: builder.query({
			query: () => ({
				url: 'product_action/list?includeDisabled=true',
				method: 'GET'
			}),
			keepUnusedDataFor: 0,
			providesTags: ['GET_TASK_WITH_DEPENDENCY_TAG']
		}),
		getProductDevJourney: builder.query({
			query: () => ({
				url: '/product-development-journey/all',
				method: 'GET'
			}),
			keepUnusedDataFor: 0,
			providesTags: ['GET_PRODUCT_DEV_JOURNEY']
		}),
		updateTaskJourney: builder.mutation({
			query: ({ body, id }) => ({
				url: `/product-development-journey/${id}`,
				method: 'PUT',
				body
			})
		}),
		cancelTask: builder.mutation({
			query: ({ body }) => ({
				url: '/product-development/cancel',
				method: 'PUT',
				body
			})
		}),
		addTaskDashboardFilter: builder.mutation({
			query: ({ body }) => ({
				url: '/task-dashboard-filter/add',
				method: 'POST',
				body
			}),
			invalidatesTags: ['GET_FILTER_LIST']
		}),
		updateTaskDashboardFilter: builder.mutation({
			query: ({ body, id }) => ({
				url: `/task-dashboard-filter/${id}`,
				method: 'PUT',
				body
			}),
			invalidatesTags: ['GET_FILTER_LIST']
		}),
		removeTaskDashboardFilter: builder.mutation({
			query: ({ id }) => ({
				url: `/task-dashboard-filter/${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['GET_FILTER_LIST']
		}),
		getFilterList: builder.query({
			query: () => ({
				url: `/task-dashboard-filter/list`,
				method: 'GET'
			}),
			providesTags: ['GET_FILTER_LIST']
		}),
		addDashboardCard: builder.mutation({
			query: ({ body }) => ({
				url: '/task-dashboard-card/add',
				method: 'POST',
				body
			}),
			invalidatesTags: ['GET_DASHBOARD_CARD_BY_USER_TYPE']
		}),
		updateDashboardCard: builder.mutation({
			query: ({ body, id }) => ({
				url: `/task-dashboard-card/${id}`,
				method: 'PUT',
				body
			}),
			invalidatesTags: ['GET_DASHBOARD_CARD_BY_USER_TYPE']
		}),
		removeTaskDashboardCard: builder.mutation({
			query: ({ id }) => ({
				url: `/task-dashboard-card/${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['GET_DASHBOARD_CARD_BY_USER_TYPE']
		}),
		getDashboardCardByUserType: builder.query({
			query: ({ userType }) => ({
				url: `/task-dashboard-card/list/${userType}`,
				method: 'GET'
			}),
			providesTags: ['GET_DASHBOARD_CARD_BY_USER_TYPE']
		}),
		getMyDashboardTasks: builder.query({
			query: () => ({
				url: '/task-dashboard-card/list/my',
				method: 'GET'
			}),
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const { data } = await queryFulfilled;
					return data;
				} catch ({ error }) {
					toastError(error?.data?.message || 'Something went wrong');
				} finally {
					dispatch(closeLoader());
				}
			},
			providesTags: ['GET_MY_DASHBOARD_TASKS']
		}),
		getFilterLogicExecutors: builder.query({
			query: ({ filterTopic, params }) => ({
				url: `/task-dashboard-filter-logic-executor/dropdown-list/${filterTopic}`,
				method: 'GET',
				params
			})
		}),
		getTaskDashboardFilterById: builder.query({
			query: ({ id }) => ({
				url: `/task-dashboard-filter/${id}`,
				method: 'GET'
			}),
			keepUnusedDataFor: 0,
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const { data } = await queryFulfilled;
					return data;
				} catch ({ error }) {
					toastError(error?.data?.message || 'Something went wrong');
				} finally {
					dispatch(closeLoader());
				}
			}
		}),
		// ============== SOP APIS ==================
		getSopList: builder.query({
			query: () => ({
				url: '/task-dashboard-sop/list',
				method: 'GET'
			}),
			providesTags: ['GET_SOP_LIST']
		}),
		getSopById: builder.query({
			query: ({ id }) => ({
				url: `/task-dashboard-sop/${id}`,
				method: 'GET'
			}),
			providesTags: ['GET_SOP_BY_ID'],
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const { data } = await queryFulfilled;
					return data;
				} catch ({ error }) {
					toastError(error?.data?.message || 'Something went wrong');
				} finally {
					dispatch(closeLoader());
				}
			}
		}),
		createSop: builder.mutation({
			query: ({ body }) => ({
				url: '/task-dashboard-sop/add',
				method: 'POST',
				body
			}),
			invalidatesTags: ['GET_SOP_LIST']
		}),
		updateSop: builder.mutation({
			query: ({ body, id }) => ({
				url: `/task-dashboard-sop/${id}`,
				method: 'PUT',
				body
			}),
			invalidatesTags: ['GET_SOP_LIST', 'GET_SOP_BY_ID']
		}),
		getSopByFilterId: builder.query({
			query: ({ filterId }) => ({
				url: `/task-dashboard-sop/filter/${filterId}`,
				method: 'GET'
			}),
			providesTags: ['GET_SOP_BY_FILTER_ID'],
			async onQueryStarted(_args, { queryFulfilled, dispatch }) {
				try {
					dispatch(openLoader());
					const { data } = await queryFulfilled;
					return data;
				} catch {
					//
				} finally {
					dispatch(closeLoader());
				}
			}
		})
	})
});

export const {
	useGetTaskConfigurationsQuery,
	useCreateNewTaskMutation,
	useUpdateExistingTaskMutation,
	useGetTaskStageQuery,
	useGetTaskWithDependencyQuery,
	useGetProductDevJourneyQuery,
	useUpdateTaskJourneyMutation,
	useCancelTaskMutation,
	useAddTaskDashboardFilterMutation,
	useUpdateTaskDashboardFilterMutation,
	useRemoveTaskDashboardFilterMutation,
	useGetFilterListQuery,
	useAddDashboardCardMutation,
	useUpdateDashboardCardMutation,
	useGetDashboardCardByUserTypeQuery,
	useRemoveTaskDashboardCardMutation,
	useGetMyDashboardTasksQuery,
	useGetFilterLogicExecutorsQuery,
	useGetTaskDashboardFilterByIdQuery,
	// SOP HOOKS
	useGetSopListQuery,
	useGetSopByIdQuery,
	useCreateSopMutation,
	useUpdateSopMutation,
	useGetSopByFilterIdQuery
} = taskConfigurationApiSlice;
